/**
 * Co-parentage Home — port de `vCpHome` du proto (l. 1982-2040).
 *
 * Phase 1, incrément 13. Première vue mode co-parentage qui câble
 * enfin l'AI Engine (incrément 11) :
 *   - card score situation + label bienveillant + résumé hebdo
 *   - badge « Avec X aujourd'hui » via `getCustodyForDate`
 *   - carrousel suggestions (max 3, dismissible — réuse `dismissSuggestion`)
 *   - card activités du jour (filtrées sur `today`)
 *   - card journal récent (2 dernières entrées) + lien vers Liaison
 *   - card sac à dos (3 premiers items + chip count missing) +
 *     lien vers `/cp/bag`
 *
 * Garde-fous CLAUDE.md §2.1 :
 *   - Tous les libellés passent par `t(key)`.
 *   - Les messages de suggestion sont déjà tone-safe (validés par
 *     `ai-engine-tone.test.ts`).
 *   - L'icône du score est neutre/positive (✅ 🟡 🔵 — jamais ❌).
 */
import { html, type TemplateResult } from 'lit-html';
import { Icon, Icons } from '../../ui/icons.js';
import styles from './HomeView.module.css';
import { getLang, t, tn } from '../../i18n/index.js';
import { firstChildNameOf, getCurrentHome } from '../../stores/homes.js';
import { interpolate } from '../ai-engine/logic.js';
import { formatMsShort } from '../../lib/date.js';
import { navigate } from '../../lib/router.js';
import { dismissSuggestion, runAnalysis, type Suggestion } from '../ai-engine/index.js';
import { getCustodyForDate } from './custody.js';
import { bumpUi } from '../../stores/ui.js';
import type { BagItem, CoparentingHomeData, CpActivity, JournalEntry } from '@nestor/shared-types';
import { editorialHero } from '../../ui/Hero.js';
import { aiSuggestion } from '../../ui/AiSuggestion.js';

/**
 * Icône du score — toujours positive (§2.1) : coche filaire teintée
 * sauge / sable / ardoise selon le niveau (audit D2, plus d'emoji).
 */
function scoreIcon(score: number): TemplateResult {
  const color =
    score >= 0.85 ? 'var(--sage-deep)' : score >= 0.6 ? 'var(--sand-deep)' : 'var(--slate-deep)';
  return Icon(Icons.check, 20, { style: `color: ${color}`, stroke: 2 });
}

function suggestionTemplate(s: Suggestion): TemplateResult {
  const onDismiss = (): void => {
    dismissSuggestion(s.id);
    bumpUi(); // force re-render — la mémoire change mais pas le store homes
  };
  // D4 : note de Nestor épinglée — contextuelle uniquement (le moteur
  // décide), signée, avec « plus tard » = dismiss.
  return aiSuggestion({
    text: s.message,
    kicker: t('cp_home_ai_suggests'),
    onDismiss,
    testid: `cp-suggestion-${s.id}`,
    dismissTestid: `cp-suggestion-dismiss-${s.id}`,
    dismissLabel: t('cancel'),
  });
}

function suggestionsCardTemplate(suggestions: readonly Suggestion[]): TemplateResult {
  if (suggestions.length === 0) return html``;
  return html`<div class=${styles.suggestionsCard} data-testid="cp-suggestions">
    <div class=${styles.suggestionsHeader}>${t('cp_home_ai_suggests')}</div>
    ${suggestions.map(suggestionTemplate)}
  </div>`;
}

function activityRowTemplate(a: CpActivity): TemplateResult {
  const isA = a.parent === 'A';
  const badgeClass = isA ? styles.cpBadgeA : styles.cpBadgeB;
  return html`<div class=${styles.activityRow} data-testid="cp-activity-${a.id}">
    <span class=${styles.activityTime}>${a.time ?? '—'}</span>
    <div class=${styles.activityMain}>
      <div class=${styles.activityTitle}>${a.title}</div>
      ${a.location ? html`<div class=${styles.activitySub}>${a.location}</div>` : html``}
    </div>
    ${a.parent ? html`<span class=${`${styles.cpBadge} ${badgeClass}`}>${a.parent}</span>` : html``}
  </div>`;
}

function activitiesCardTemplate(activities: readonly CpActivity[]): TemplateResult {
  return html`<div class=${styles.card} data-testid="cp-activities-card">
    <div class=${styles.cardTitle}>
      ${t('cp_home_today_activities')}
      <span class=${styles.chip}>${activities.length}</span>
    </div>
    ${activities.length === 0
      ? html`<div class=${styles.empty}>${t('cp_home_no_activities')}</div>`
      : activities.map(activityRowTemplate)}
  </div>`;
}

function journalEntryTemplate(e: JournalEntry): TemplateResult {
  const date = formatMsShort(e.at, getLang());
  return html`<div class=${styles.journalEntry} data-testid="cp-journal-${e.id}">
    <div class=${styles.journalMeta}>${e.who ?? ''} · ${date}</div>
    <div class=${styles.journalText}>${e.text}</div>
  </div>`;
}

function journalCardTemplate(entries: readonly JournalEntry[]): TemplateResult {
  const onView = (): void => {
    navigate('/cp/liaison');
  };
  return html`<div class=${styles.card} data-testid="cp-journal-card">
    <div class=${styles.cardTitle}>${t('cp_home_recent_journal')}</div>
    ${entries.length === 0
      ? html`<div class=${styles.empty}>${t('cp_home_no_journal')}</div>`
      : entries.map(journalEntryTemplate)}
    <div class=${styles.cardCtaRow}>
      <button type="button" class=${styles.cardCta} data-testid="cp-view-journal" @click=${onView}>
        ${t('cp_home_view_journal')}
      </button>
    </div>
  </div>`;
}

function bagItemRowTemplate(b: BagItem): TemplateResult {
  return html`<div class=${styles.bagItem} data-testid="cp-bag-item-${b.id}">
    <div class=${`${styles.bagStatus} ${b.status === 'ok' ? styles.ok : styles.missing}`}>
      ${b.status === 'ok' ? '✓' : '!'}
    </div>
    <span class=${styles.bagIcon}>${b.icon ?? '📦'}</span>
    <div class=${styles.bagInfo}>
      <div class=${styles.bagItemName}>${b.item}</div>
      <div class=${styles.bagLocation}>${b.location}</div>
    </div>
  </div>`;
}

function bagCardTemplate(bag: readonly BagItem[]): TemplateResult {
  const missingCount = bag.filter((b) => b.status === 'missing').length;
  const onManage = (): void => {
    navigate('/cp/bag');
  };
  return html`<div class=${styles.card} data-testid="cp-bag-card">
    <div class=${styles.cardTitle}>
      ${t('cp_home_bag_title')}
      ${missingCount > 0
        ? html`<span class=${`${styles.chip} ${styles.chipMissing}`}
            >${missingCount.toString()} ${tn('cp_home_bag_missing', missingCount)}</span
          >`
        : html`<span class=${`${styles.chip} ${styles.chipComplete}`}
            >${t('cp_home_bag_complete')}</span
          >`}
    </div>
    ${bag.slice(0, 3).map(bagItemRowTemplate)}
    <div class=${styles.cardCtaRow}>
      <button type="button" class=${styles.cardCta} data-testid="cp-manage-bag" @click=${onManage}>
        ${t('cp_home_manage_bag')}
      </button>
    </div>
  </div>`;
}

export function renderCpHomeView(): TemplateResult {
  const home = getCurrentHome();
  if (!home || home.mode !== 'coparenting') {
    // Cas dégradé — `reconcileRouteWithHome` du shell devrait déjà avoir
    // redirigé. On rend un fragment minimal mais visible (taille non
    // nulle) pour ne pas casser les tests qui attendent `view-cp-home`.
    return html`<section class=${styles.view} data-testid="view-cp-home">
      <p class=${styles.empty}>${t('cp_home_greeting')} 👋</p>
    </section>`;
  }

  const data = home.data as CoparentingHomeData;
  const now = new Date();
  const analysis = runAnalysis(data, now);

  const todayParentName = getCustodyForDate(now, data.custody);
  const todayIso = now.toISOString().slice(0, 10);
  const todayActs = (data.activities ?? []).filter((a) => a.date === todayIso);
  const recentJ = (data.journal ?? []).slice(0, 2);

  return html`
    <section class=${styles.view} data-testid="view-cp-home">
      ${editorialHero({
        badge: { mode: 'coparenting', label: home.name },
        dateLine: t('cp_home_dateline'),
        // D1 + B1 : « La semaine de Chloé. » — plus jamais le premier
        // mot du nom du foyer salué comme un prénom (« Bonjour, Garde. »).
        title: t('cp_home_hero_title'),
        accent: ((): string => {
          const child = firstChildNameOf(home);
          return child
            ? interpolate(t('cp_home_hero_accent_child'), { name: child })
            : t('cp_home_hero_accent_fallback');
        })(),
      })}

      <div class=${styles.card} data-testid="cp-score-card">
        <div class=${styles.scoreRow}>
          <div class=${styles.scoreIcon} data-testid="cp-score-icon">
            ${scoreIcon(analysis.score)}
          </div>
          <div class=${styles.scoreText}>
            <div class=${styles.scoreLabel} data-testid="cp-score-label">
              ${analysis.scoreLabel}
            </div>
            <div class=${styles.scoreSummary} data-testid="cp-score-summary">
              ${analysis.summary}
            </div>
          </div>
        </div>
        ${todayParentName
          ? html`<div class=${styles.todayParent} data-testid="cp-today-parent">
              ${Icon(Icons.users, 13)} ${t('cp_home_with')} ${todayParentName}
            </div>`
          : html`<div class=${styles.todayParent} data-testid="cp-no-custody">
              ${t('cp_home_no_custody')}
            </div>`}
      </div>

      ${suggestionsCardTemplate(analysis.suggestions)} ${activitiesCardTemplate(todayActs)}
      ${journalCardTemplate(recentJ)} ${bagCardTemplate(data.bag ?? [])}
    </section>
  `;
}
